import { useEffect, useMemo } from "react";
import { BaseFormControlledModal } from "../../../../../../common/components/base_form_controlled_modal";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { DefaultFormDatePickerInput } from "../../../../../../common/components/default_form_date_picker_input";
import { DefaultFormRadioGroupInput } from "../../../../../../common/components/default_form_radio_group_input";
import { StyledDataGrid } from "../../../../../../common/components/styled_data_grid";
import { IconButton, IconContainerProps, Rating, styled } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-pro";
import { ItemPesquisaClienteLista } from "../../../../domain/models/item_pesquisa_cliente_lista";
import { AssistenciaTecnicaPesquisaLista } from "../../../../domain/models/assistencia_tecnica_pesquisa_lista";
import { AssistenciaPesquisaSatisfacaoForm, AssistenciaPesquisaSatisfacaoSchema } from "../../../schemas/AssistenciaTecnica_cadastro/assistencia_pesquisa_satisfacacao";

import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import SentimentSatisfiedIcon from '@mui/icons-material/SentimentSatisfied';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAltOutlined';
import SentimentVerySatisfiedIcon from '@mui/icons-material/SentimentVerySatisfied';
import FormContainer from "../../../../../../common/components/form_container";
import DynamicIcon from "../../../../../../common/components/dynamic_icon";
import dayjs from "dayjs";
import DefaultFormInput from "../../../../../../common/components/default_form_input";
import { LegendComponent } from "../../../../../../common/components/legend_component";
import { LegendItemComponent } from "../../../../../../common/components/legend_item_component";
import { LegendIcon } from "../../../../../../common/components/legend_icon";

/**
 * Icons
 */

const customIcons: {
  [index: string]: {
    textColor: string;
    icon: React.ReactElement<unknown>;
    label: string;
  };
} = {
  1: {
    textColor: 'text-red-500',
    icon: <SentimentVeryDissatisfiedIcon className="text-red-500" />,
    label: 'Very Dissatisfied',
  },
  2: {
    textColor: 'text-orange-500',
    icon: <SentimentDissatisfiedIcon className="text-orange-500" />,
    label: 'Dissatisfied',
  },
  3: {
    textColor: 'text-yellow-500',
    icon: <SentimentSatisfiedIcon className="text-yellow-500" />,
    label: 'Neutral',
  },
  4: {
    textColor: 'text-green-300',
    icon: <SentimentSatisfiedAltIcon className="text-green-300" />,
    label: 'Satisfied',
  },
  5: {
    textColor: 'text-green-600',
    icon: <SentimentVerySatisfiedIcon className="text-green-600" />,
    label: 'Very Satisfied',
  },
};

function IconContainer(props: IconContainerProps) {
  const { value, ...other } = props;
  const isSelected = other.className?.includes('MuiRating-iconFilled');
  return (
    <span {...other}>
      <div className="flex flex-col items-center">
        <span className="flex">
          {customIcons[value].icon}
        </span>
        <span className={`${isSelected ? customIcons[value].textColor : ''} text-xs`}>
          {typeof value === 'number' && value}
        </span>
      </div >
    </span >
  );
}

const StyledRating = styled(Rating)(({ theme }) => ({
  '& .MuiRating-iconEmpty .MuiSvgIcon-root': {
    color: theme.palette.action.disabled,
  },
}));

/**
 * GenerateColumns
 */

type GenerateColumnsProps = {
  onActionNotaChange: (idItemPesquisaCliente: number, nota: number | null) => void;
  onActionNotaClear: (idItemPesquisaCliente: number) => void;
};

const GenerateColumns = ({
  onActionNotaChange,
  onActionNotaClear,
}: GenerateColumnsProps): GridColDef[] => {
  return [
    {
      field: "descricao",
      filterable: false,
      headerName: "Perguntas",
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => (
        <div className="w-full h-full flex break-normal" >
          {params.value}
        </div>
      )
    },
    {
      field: "nota",
      filterable: false,
      headerName: "Nota",
      width: 200,
      sortable: false,
      align: 'center',
      valueGetter: (_, rows) => {
        return [rows['idItemPesquisaCliente'] as number, rows['nota'] as number];
      },
      renderCell: ({ value }) => {
        return (
          <StyledRating
            className="w-full h-full"
            getLabelText={(value: number) => customIcons[value].label}
            highlightSelectedOnly={true}
            value={value[1]}
            slotProps={{
              root: {
                className: "flex flex-row justify-center items-center gap-2",
              },
              icon: {
                component: IconContainer,
              }
            }}
            onChange={(_, newNota) => { onActionNotaChange(value[0], newNota); }}
          />
        );
      },
    },
    {
      field: "acoes",
      filterable: false,
      headerName: "Ações",
      sortable: false,
      width: 60,
      valueGetter: (_, rows) => rows['idItemPesquisaCliente'] as number,
      renderCell: ({ value }) => {
        return (
          <div className="flex items-center justify-center w-full h-full gap-2">
            <IconButton onClick={() => { onActionNotaClear(value); }}>
              <DynamicIcon nameIcon="DeleteForeverRounded" size={25} className="text-orange-500" />
            </IconButton>
          </div>
        );
      }
    },
  ];
}

/**
 * PesquisaSatisfacaoClienteModal
 */

type Props = {
  open: boolean;
  saving: boolean;
  loading: boolean;
  selectedPesquisaSatisfacaoCliente: AssistenciaTecnicaPesquisaLista | null;
  itensPesquisaCliente: ItemPesquisaClienteLista[];
  onClose: () => void;
  onSubmit: (form: AssistenciaPesquisaSatisfacaoForm) => void;
};

export const PesquisaSatisfacaoClienteModal = ({
  open,
  saving,
  loading,
  selectedPesquisaSatisfacaoCliente,
  itensPesquisaCliente,
  onClose,
  onSubmit,
}: Props) => {

  const hookForm = useForm<AssistenciaPesquisaSatisfacaoForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(AssistenciaPesquisaSatisfacaoSchema),
  });

  const [itens] = hookForm.watch(['itens'])

  const percPonto = useMemo(() => {
    if (itens === undefined || itens === null || itens.length === 0) return 0;
    const reduceFunc = (acc: number, x: any) => x.nota !== null ? acc + x.nota : acc;
    return Math.round((itens.reduce(reduceFunc, 0) / (itens.length * 5)) * 100) / 100;
  }, [
    itens,
  ]);

  const columnDefinition = useMemo(() => {
    return GenerateColumns({
      onActionNotaChange: (idItemPesquisaCliente: number, nota: number | null) => {
        const items = hookForm.getValues('itens');
        const itemIndex = items.findIndex(i => i.idItemPesquisaCliente === idItemPesquisaCliente);
        if (itemIndex !== -1) {
          items[itemIndex].nota = nota;
          hookForm.setValue('itens', items);
        }
      },
      onActionNotaClear: (idItemPesquisaCliente: number) => {
        const items = hookForm.getValues('itens');
        const itemIndex = items.findIndex(i => i.idItemPesquisaCliente === idItemPesquisaCliente);
        if (itemIndex !== -1) {
          items[itemIndex].nota = null;
          hookForm.setValue('itens', items);
        }
      },
    });
  }, [
    hookForm,
  ]);

  const statusOptions = useMemo(() => {
    return [
      {
        id: 0,
        label: 'Em Aberto',
        style: {
          backgroundColor: '#f8cb00',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
      {
        id: 1,
        label: 'Finalizada',
        style: {
          backgroundColor: '#45b6af',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
    ];
  }, [
  ]);

  useEffect(() => {
    if (open) {
      if (selectedPesquisaSatisfacaoCliente && itensPesquisaCliente) {
        const {
          codigo,
          descricao,
          data,
          status,
        } = selectedPesquisaSatisfacaoCliente;

        hookForm.setValue('pesquisa', `${codigo} - ${descricao}`);
        hookForm.setValue('data', data ? dayjs.utc(data).local().toDate() : null);
        hookForm.setValue('status', status ?? 0);
        hookForm.setValue('itens', itensPesquisaCliente.map(i => ({
          idItemPesquisaCliente: i.idItemPesquisaCliente,
          descricao: i.descricao,
          nota: i.nota,
        })));
      } else {
        hookForm.reset({ status: 0 });
      }
    }
  }, [
    hookForm,
    open,
    selectedPesquisaSatisfacaoCliente,
    itensPesquisaCliente,
  ]);

  return (
    <BaseFormControlledModal
      open={open}
      saving={saving}
      loading={loading}
      width="80rem"
      title='Pesquisa de Satisfação do Cliente'
      onClose={onClose}
      onSubmit={hookForm.handleSubmit(onSubmit)}>
      <FormContainer title="Informações" arrangement="fullRow" gap="small">
        <>
          <DefaultFormInput
            name='pesquisa'
            label='Pesquisa'
            readOnly={true}
            reactHookForm={hookForm}
          />

          <DefaultFormDatePickerInput
            name='data'
            label='Data'
            reactHookForm={hookForm}
          />

          <div className="w-full flex flex-row gap-2 justify-between">
            <DefaultFormRadioGroupInput
              name='status'
              label='Status'
              options={statusOptions}
              reactHookForm={hookForm}
            />

            <div className="flex flex-row gap-2 items-end pb-2 font-bold text-black dark:text-white">
              <span>Total de Pontos:</span>
              <span>
                {
                  new Intl.NumberFormat("pt-BR", {
                    style: 'percent',
                    minimumFractionDigits: 2,
                  }).format(percPonto)
                }
              </span>
            </div>
          </div>

          <div className="w-full flex flex-row justify-between font-bold text-black dark:text-white">
            <LegendComponent>
              <LegendItemComponent
                icon={<LegendIcon color='red' title={"1."} />}
                label='Péssimo' />

              <LegendItemComponent
                icon={<LegendIcon color='orange' title={"2."} />}
                label='Ruim' />

              <LegendItemComponent
                icon={<LegendIcon color='yellow' title={"3."} />}
                label='Regular' />

              <LegendItemComponent
                icon={<LegendIcon color='lightGreen' title={"4."} />}
                label='Bom' />

              <LegendItemComponent
                icon={<LegendIcon color='green' title={"5."} />}
                label='Excelente' />
            </LegendComponent>
          </div>

          <StyledDataGrid
            tableName="pesquisa_satisfacao_cliente_modal_grid"
            autoHeight={true}
            disableRowSelectionOnClick={true}
            disableColumnMenu={false}
            getRowId={(row: any) => row.idItemPesquisaCliente}
            columns={columnDefinition}
            getRowHeight={() => 'auto'}
            loading={loading || saving}
            rows={itens ?? []}
            disableColumnSorting={true}
            disableColumnResize={true}
            showToolbar={false}
          />
        </>
      </FormContainer>
    </BaseFormControlledModal>
  );
}